// Session-scoped registry of molecular analyses, used by the
// Cross-Phase Continuity Banner. We hash the user's uploaded
// genotype CSV (SHA-256, client-side via SubtleCrypto) and stash
// `{analysisId → {phase, genotypeHash, summary}}` in sessionStorage.
//
// On Phase 2/3/4 results, we look up the current analysis's
// genotype hash, then scan the registry for a Phase 1 entry with
// the same hash → render banner with that summary.
//
// Niranjan, Feb 2026 — BATCH-NEXT-1.

const KEY = 'bkagrid:molecular_session_v1';


export async function sha256OfFile(file) {
  if (!file) return null;
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}


export function getRegistry() {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) || '{}');
  } catch {
    return {};
  }
}


export function recordAnalysis(analysisId, record) {
  if (!analysisId || !record) return;
  const reg = getRegistry();
  reg[analysisId] = { ...record, recordedAt: Date.now() };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(reg));
  } catch {
    // QuotaExceeded — drop oldest 50 % and retry once
    const entries = Object.entries(reg).sort((a, b) => (a[1].recordedAt || 0) - (b[1].recordedAt || 0));
    const half = Math.floor(entries.length / 2);
    const compact = Object.fromEntries(entries.slice(half));
    compact[analysisId] = record;
    try { sessionStorage.setItem(KEY, JSON.stringify(compact)); } catch (e) {
      // sessionStorage full + retry failed (e.g. browser in private mode
      // or storage quota exhausted). Log so devtools surface the issue,
      // but don't throw — the registry is best-effort cache.
      // eslint-disable-next-line no-console
      console.warn('molecularSessionRegistry: sessionStorage write failed after compaction', e);
    }
  }
}


export function getAnalysisRecord(analysisId) {
  if (!analysisId) return null;
  return getRegistry()[analysisId] || null;
}


export function findPhase1ByGenotypeHash(genotypeHash) {
  if (!genotypeHash) return null;
  const reg = getRegistry();
  for (const [id, r] of Object.entries(reg)) {
    if (r.phase === 'phase1' && r.genotypeHash === genotypeHash) {
      return { analysisId: id, ...r };
    }
  }
  return null;
}


// Phase 1 summary extractor — produces a compact object the banner
// can render without re-fetching the full analysis. Called by
// `Phase1UploadCard` on a successful run.
export function buildPhase1Summary(phase1Result) {
  if (!phase1Result) return null;
  const qc = phase1Result.genotype_qc?.summary || {};
  const heritabilityTable = phase1Result.heritability_table || [];
  const phenoQc = phase1Result.phenotype_qc_table || [];

  // Per-trait H² band
  const traits = {};
  heritabilityTable.forEach(row => {
    const trait = row.Trait || row.trait;
    const h2 = row['H² (broad-sense)'] ?? row['h2_broad_sense'];
    if (!trait || h2 == null) return;
    let band;
    if (h2 >= 0.60) band = 'high';
    else if (h2 >= 0.30) band = 'moderate';
    else band = 'low';
    traits[trait] = { h2, band };
  });

  const warnings = [];
  const lowH2 = Object.entries(traits).filter(([, v]) => v.band === 'low').map(([t]) => t);
  if (lowH2.length) warnings.push(`H² < 0.30 for: ${lowH2.join(', ')}`);
  const failedNormality = phenoQc.filter(r => r['Normal? (α=0.05)'] === 'No').map(r => r.Trait);
  if (failedNormality.length) warnings.push(`Normality failed for: ${failedNormality.join(', ')}`);
  const retainPct = qc.n_markers_retained && qc.n_markers_input
    ? qc.n_markers_retained / qc.n_markers_input : 1;
  if (retainPct < 0.5) warnings.push(`< 50 % markers retained (${qc.n_markers_retained}/${qc.n_markers_input})`);

  return {
    analysisId: null, // filled in by caller
    nRetained: qc.n_markers_retained || 0,
    nInput: qc.n_markers_input || 0,
    nIndividuals: qc.n_individuals || phase1Result.input_summary?.n_genotype_individuals || 0,
    traits,
    warnings,
    cleanState: warnings.length === 0,
  };
}
